import type { ApplogValue } from '../../applog/datom-types.ts'
import type { ISchemaAdapter, VMAttributeDef } from '../types.ts'

/**
 * Pattern for creating a schema adapter from an ArkType schema.
 *
 * ArkType is NOT a runtime dependency of @wovin/core, so this adapter
 * works by accepting the ArkType type object directly.
 *
 * Usage:
 * ```ts
 * import { type } from 'arktype'
 * import { createArkTypeAdapter } from '@wovin/core/viewmodel/adapters/arktype'
 *
 * const MySchema = type({
 *   name: 'string',
 *   age: 'number | undefined',
 * })
 *
 * const adapter = createArkTypeAdapter(MySchema, 'myEntity')
 * ```
 */
export function createArkTypeAdapter<T extends Record<string, ApplogValue>>(
	_arkTypeSchema: { infer: T; (input: unknown): T | string },
	entityPrefix: string,
	options?: {
		/** Override at-paths for specific attributes */
		atOverrides?: Record<string, string>
		/** Attribute definitions (must be provided for ArkType since we can't introspect the type) */
		attributes: VMAttributeDef[]
		/** Default values */
		defaults?: Partial<T>
	},
): ISchemaAdapter<T> {
	return {
		getAttributeDefs: () => options?.attributes ?? [],
		getDefaults: () => (options?.defaults ?? {}) as Partial<T>,
		getEntityPrefix: () => entityPrefix,
		createValidator: () => (value: unknown): value is T => {
			const result = _arkTypeSchema(value as any)
			return typeof result !== 'string'
		},
	}
}
